import { pdf } from "@react-pdf/renderer";
import { LoadingSpinner } from "components/LoadingSpinner";
import { Button } from "primereact/button";
import { useState } from "react";
import { useSelector } from "react-redux";
import { Navigate, useNavigate } from "react-router-dom";
import { useGetAccountByIdQuery } from "store/queries/account";
import { useGetMyTrainingQuery } from "store/queries/training";
import { userSelector } from "store/slices/user";
import styled, { createGlobalStyle } from "styled-components";
import {
  Body,
  Certificate,
  Certifies,
  DateLine,
  Eyebrow,
  formatCertificateDate,
  Hospital,
  Name,
  Ribbon,
  Title,
  Version,
} from "training/components/CertificateParts";
import {
  buildLinkedInAddToProfileUrl,
  publicCredentialUrl,
} from "training/linkedIn";
import { QUIZ_PATH } from "training/quizHelpers";

import { CERTIFICATE_BODY, CertificatePdf } from "./CertificatePdf";

export const CERTIFICATE_PDF_FILE_NAME = "Clear-Certified-certificate.pdf";

/** Only the certificate prints: everything else on the page (the app's bars, menus, buttons) is hidden. */
const PrintStyle = createGlobalStyle`
  @media print {
    body * {
      visibility: hidden !important;
    }

    .clear-certificate,
    .clear-certificate * {
      visibility: visible !important;
    }

    .clear-certificate {
      position: fixed;
      inset: 0;
      margin: 0;
      box-shadow: none;
    }

    @page {
      size: landscape;
      margin: 12mm;
    }
  }
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  padding: 24px 0;
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 12px;
`;

/** "Share this link": the public verification URL, read-only, with a Copy button. */
const Share = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  width: 100%;
  max-width: 560px;

  label {
    color: var(--brand-gray-600);
    font-size: 0.85rem;
  }
`;

const ShareRow = styled.div`
  display: flex;
  gap: 8px;
  width: 100%;

  input {
    flex: 1;
    min-width: 0;
  }
`;

const ErrorText = styled.p`
  margin: 0;
  color: var(--red-600, #dc2626);
`;

/**
 * CLE-3015 — /training/certificate. The certified user's certificate: name, hospital, "Clear Certified",
 * the date and (small) the curriculum version, with Print (a print stylesheet that leaves only the
 * certificate), Download PDF (rendered here with @react-pdf/renderer), Add to LinkedIn (the pre-filled
 * "Add to profile" link, new tab), the public verification link with Copy, and Back to Estimates. Anyone not
 * certified is sent to the knowledge check. The route is HospitalStaff-only (WithRoleProtection).
 */
export function CertificatePage() {
  const navigate = useNavigate();
  const { currentAccount, name } = useSelector(userSelector);
  const { data, isLoading, isFetching, isError } =
    useGetMyTrainingQuery(currentAccount);
  const { data: accountData } = useGetAccountByIdQuery(currentAccount ?? "", {
    skip: !currentAccount,
  });
  const [downloading, setDownloading] = useState(false);
  const [downloadFailed, setDownloadFailed] = useState(false);
  const [copied, setCopied] = useState(false);

  const record = data?.data;
  // "View your certificate" is pressed right after a pass, while the pass's refetch of the record is still in
  // flight: the cached record still says not certified. Wait for the fresh one before sending anyone to the
  // knowledge check, or a slow refetch bounces a just-certified trainee to the quiz and from there to the
  // hospital view (CLE-3015 nightly walk, 2026-10-22).
  if (isLoading || (isFetching && !record?.clearCertifiedAt)) {
    return <LoadingSpinner />;
  }
  if (isError || !record?.clearCertifiedAt) {
    return <Navigate to={QUIZ_PATH} replace />;
  }

  const content = {
    name: name ?? "",
    hospitalName: accountData?.data?.name ?? "",
    certifiedDate: formatCertificateDate(record.clearCertifiedAt),
    curriculumVersion: record.clearCertifiedVersion,
  };

  // The credential id is assigned on certification (and on this GET for anyone certified before it
  // existed), so it is normally present; without one there is nothing to share.
  const credentialId = record.clearCertifiedCredentialId;
  const shareUrl = credentialId
    ? publicCredentialUrl(window.location.origin, credentialId)
    : null;
  const linkedInUrl = credentialId
    ? buildLinkedInAddToProfileUrl({
        credentialId,
        certifiedAt: record.clearCertifiedAt,
        origin: window.location.origin,
      })
    : null;

  const handleCopy = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // The clipboard can be refused (no HTTPS, a locked-down browser); the link stays selectable.
    }
  };

  const handleDownload = async () => {
    setDownloading(true);
    setDownloadFailed(false);
    try {
      const blob = await pdf(<CertificatePdf {...content} />).toBlob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = CERTIFICATE_PDF_FILE_NAME;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(url), 0);
    } catch (error) {
      console.error("[Clear Certified] the certificate PDF failed", error);
      setDownloadFailed(true);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Wrapper>
      <PrintStyle />
      <Certificate
        className="clear-certificate"
        aria-label="Clear Certified certificate"
      >
        <Ribbon data-ribbon>
          <i className="pi pi-verified" aria-hidden />
        </Ribbon>
        <Eyebrow>Certificate of completion</Eyebrow>
        <Title>Clear Certified</Title>
        <Certifies>This certifies that</Certifies>
        <Name data-testid="certificate-name">{content.name}</Name>
        {content.hospitalName ? (
          <Hospital data-testid="certificate-hospital">
            {content.hospitalName}
          </Hospital>
        ) : null}
        <Body>{CERTIFICATE_BODY}</Body>
        <DateLine data-testid="certificate-date">
          {content.certifiedDate}
        </DateLine>
        {content.curriculumVersion ? (
          <Version>Curriculum version {content.curriculumVersion}</Version>
        ) : null}
      </Certificate>
      <Actions>
        <Button
          type="button"
          icon="pi pi-print"
          label="Print"
          className="p-button-outlined"
          onClick={() => window.print()}
        />
        <Button
          type="button"
          icon="pi pi-download"
          label="Download PDF"
          className="p-button-outlined"
          loading={downloading}
          onClick={handleDownload}
        />
        {linkedInUrl ? (
          <a
            href={linkedInUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="p-button p-component p-button-outlined"
            data-testid="certificate-linkedin"
          >
            <i className="pi pi-linkedin p-button-icon p-button-icon-left" />
            <span className="p-button-label">Add to LinkedIn</span>
          </a>
        ) : null}
        <Button
          type="button"
          label="Back to Estimates"
          onClick={() =>
            navigate(
              currentAccount ? `/hospitals/view/${currentAccount}/orders` : "/"
            )
          }
        />
      </Actions>
      {shareUrl ? (
        <Share>
          <label htmlFor="certificate-share-link">Share this link</label>
          <ShareRow>
            <input
              id="certificate-share-link"
              className="p-inputtext p-component"
              value={shareUrl}
              readOnly
              onFocus={(event) => event.target.select()}
            />
            <Button
              type="button"
              icon={copied ? "pi pi-check" : "pi pi-copy"}
              label={copied ? "Copied" : "Copy"}
              className="p-button-outlined"
              onClick={handleCopy}
            />
          </ShareRow>
        </Share>
      ) : null}
      {downloadFailed ? (
        <ErrorText role="alert">
          The PDF could not be created. Use Print and choose &quot;Save as
          PDF&quot; instead.
        </ErrorText>
      ) : null}
    </Wrapper>
  );
}
